import { Row, Space, Typography } from 'antd';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { RedoOutlined } from '@ant-design/icons';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { IndividualType } from 'store/individuals/individual';
import { getRequiredMessage } from 'views/declarations/utils/validation-utils';
import FormAutocomplete from 'components/new-ui/composed/formAutocomplete/FormAutocomplete';
import { DefaultOptionType } from 'antd/lib/select';
import { Team } from 'store/teams/types';
import { assignManifestToOwner } from 'store/manifests/client';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';
import { getTeamById } from 'store/teams/client';
import { Manifest } from 'store/manifests/types';

type Props = {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    manifest: Manifest;
};

const getValidationSchema = () =>
    Yup.object({
        teamMemberId: Yup.string().required(getRequiredMessage('teamMember')),
    });

enum FieldNames {
    TEAM_ID = 'teamId',
    TEAM_MEMBER_ID = 'teamMemberId',
}

const ALLOWED_ASSIGNEE_ROLES: IndividualType[] = [
    IndividualType.TEAM_MANAGER,
    IndividualType.JOB_MANAGER,
    IndividualType.CLEARANCE_AGENT,
];

const AssignManifestModal = ({ open, onClose, onSuccess, manifest }: Props) => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [selectedTeam, setSelectedTeam] = useState<Team>();
    const [individualInput, setIndividualInput] = useState('');
    const [filteredTeamMembers, setFilteredTeamMembers] = useState<DefaultOptionType[]>([]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: { teamId: '', teamMemberId: '' },
        validationSchema: getValidationSchema(),
        onSubmit: async (values, helpers) => {
            try {
                showGlobalOverlay({
                    type: 'LoadingOverlay',
                    payload: {
                        message: 'Assigning manifest to team member...',
                    },
                });

                await assignManifestToOwner(manifest.id, values.teamMemberId);

                Notification({
                    type: 'success',
                    placement: 'top',
                    description: 'Successfully assigned manifest',
                    messageTitle: 'Success',
                });

                helpers.resetForm();
                setIndividualInput('');
                setFilteredTeamMembers([]);

                onSuccess?.();
                onClose();
            } catch (error) {
                const errorInfo = error instanceof AxiosError && error.response?.data?.message;
                Notification({
                    type: 'error',
                    placement: 'top',
                    description: errorInfo || 'Could not assign manifest. Please try again.',
                    messageTitle: 'Error',
                });
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    const handleAssignManifest = () => {
        formik.submitForm();
    };

    const handleCloseModal = () => {
        formik.resetForm();
        setIndividualInput('');
        setFilteredTeamMembers([]);
        onClose();
    };

    const teamMembersOptions: DefaultOptionType[] = useMemo(
        () =>
            selectedTeam?.members
                // Show only active team members
                .filter((m) => ALLOWED_ASSIGNEE_ROLES.includes(m.role) && m.individual.active)
                .map((m) => ({
                    label: m.individual.fullName ?? m.individual.email,
                    value: m.individual.id,
                })) ?? [],

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedTeam, open]
    );

    const handleSelectIndividual = (val: string, option?: DefaultOptionType) => {
        formik.setFieldValue(FieldNames.TEAM_MEMBER_ID, val);
        setIndividualInput(String(option?.label ?? ''));
    };

    const handleSearchTeamMembers = (val: string) => {
        setIndividualInput(val);

        if (formik.values.teamMemberId) {
            formik.setFieldValue(FieldNames.TEAM_MEMBER_ID, undefined);
            formik.setFieldTouched(FieldNames.TEAM_MEMBER_ID, true, false);
        }

        const q = val.trim().toLowerCase();
        if (!q) {
            setFilteredTeamMembers(teamMembersOptions);
            return;
        }

        setFilteredTeamMembers(teamMembersOptions.filter((option) => String(option.label).toLowerCase().includes(q)));
    };

    const handleClearIndividual = () => {
        setIndividualInput('');
        formik.setFieldValue(FieldNames.TEAM_MEMBER_ID, undefined);
    };

    // Get team data
    useEffect(() => {
        if (!open) return;

        formik.setFieldValue(FieldNames.TEAM_ID, manifest.team?.id, false);

        (async () => {
            if (!manifest.team?.id) return;

            const team = await getTeamById(manifest.team.id);
            setSelectedTeam(team);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manifest.team?.id, open]);

    // Sync the filtered team member options with team changes
    useEffect(() => {
        setFilteredTeamMembers(teamMembersOptions);
    }, [teamMembersOptions]);

    const teamMemberEl = (
        <Row justify="space-between">
            <label htmlFor={FieldNames.TEAM_MEMBER_ID}>Team Member:</label>
            <div>
                <FormAutocomplete
                    required
                    id={FieldNames.TEAM_MEMBER_ID}
                    inputPlaceholder="Please select a team member"
                    style={{ minWidth: 375 }}
                    fieldMeta={formik.getFieldMeta(FieldNames.TEAM_MEMBER_ID)}
                    onBlur={() => formik.setFieldTouched(FieldNames.TEAM_MEMBER_ID, true)}
                    onClear={handleClearIndividual}
                    onSearch={handleSearchTeamMembers}
                    onSelect={handleSelectIndividual}
                    options={filteredTeamMembers}
                    value={individualInput}
                />
            </div>
        </Row>
    );

    return (
        <ConfirmationModal
            destroyOnClose
            open={open}
            icon={<RedoOutlined />}
            title="Assign"
            confirmButtonText="Assign"
            confirmationButtonDisabled={!formik.isValid}
            confirmationButtonLoading={formik.isSubmitting}
            onCancel={handleCloseModal}
            onConfirm={handleAssignManifest}
            content={
                <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
                    <Space
                        direction="vertical"
                        size={16}
                        style={{
                            textAlign: 'left',
                            width: '100%',
                        }}
                    >
                        <Typography.Text>
                            Please select a team and a team member to assign this manifest to
                        </Typography.Text>
                        <Row justify="space-between">
                            <label htmlFor="team">Team:</label>
                            <div>
                                <FormAutocomplete disabled style={{ minWidth: 375 }} value={selectedTeam?.name ?? ''} />
                            </div>
                        </Row>
                        {teamMemberEl}
                    </Space>
                </form>
            }
        />
    );
};

export default AssignManifestModal;
